"use client";

import {
  type ComponentProps,
  createContext,
  type FocusEvent,
  type ReactNode,
  use,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { AlertCircleIcon } from "@/components/icons/alert-circle";
import { CheckCircleIcon } from "@/components/icons/check-circle";
import type { IconComponent } from "@/components/icons/create-icon";
import { InfoIcon } from "@/components/icons/info";
import { MegaphoneIcon } from "@/components/icons/megaphone";
import { XIcon } from "@/components/icons/x";
import { Button } from "@/components/ui/button";
import { MENU_SIDE_ANIMATION_CLASS } from "@/components/ui/menu-animation";
import {
  getServerToasts,
  getToasts,
  removeToast,
  subscribeToasts,
  TOAST_DURATION_MS,
  type ToastItem,
} from "@/lib/client/toast";
import { cn } from "@/lib/shared/utils";

/**
 * 画面上部に重ねて出す通知の役割。色とアイコンで役割を見分けられるようにする。
 * success、error、infoはトースト（使い分けは@/lib/client/toast）、announcementはシステム管理者のお知らせ。
 */
export type NoticeTone = "success" | "error" | "info" | "announcement";

const NOTICE_TONES: Record<
  NoticeTone,
  { icon: IconComponent; className: string }
> = {
  success: {
    icon: CheckCircleIcon,
    className: "[--notice-tint:var(--success-subtle)] text-success-foreground",
  },
  error: {
    icon: AlertCircleIcon,
    className: "[--notice-tint:var(--danger-subtle)] text-danger-foreground",
  },
  info: {
    icon: InfoIcon,
    className: "[--notice-tint:var(--info-subtle)] text-info-foreground",
  },
  announcement: {
    icon: MegaphoneIcon,
    className: "[--notice-tint:var(--warning-subtle)] text-warning-foreground",
  },
};

// 枠線を付けず、広く薄い影だけで面から浮かせる。役割の色はダークモードで半透明なので、
// 下の画面が透けないように不透明な面の上へ重ねる。ボタンはサイドバーなどの操作ボタンと
// 同じ32pxなので、右の余白はボタン自身の余白と合わせて左と同じ見た目にする。
const NOTICE_CARD_CLASS =
  "pointer-events-auto relative flex w-fit max-w-2xl min-w-[min(20rem,100%)] flex-wrap items-start gap-2 rounded-lg bg-popover bg-[linear-gradient(var(--notice-tint),var(--notice-tint))] py-2 pr-2 pl-4 text-sm shadow-composer";

/** 通知の中に置く文字のボタン。サイドバーなどの操作ボタンと同じ大きさで、閉じるボタンの左に並べる。 */
export function NoticeTextButton(props: ComponentProps<typeof Button>) {
  return <Button type="button" variant="ghost" {...props} />;
}

/** 通知の見た目。トーストもお知らせも、お知らせを作る画面のプレビューもこれで描く。 */
export function NoticeCard({
  tone,
  title,
  description,
  action,
  closeLabel,
  onClose,
  className,
  ...props
}: Omit<ComponentProps<"div">, "title"> & {
  tone: NoticeTone;
  title: ReactNode;
  description?: ReactNode;
  /** 本文の右端に置く操作。 */
  action?: ReactNode;
  closeLabel: string;
  onClose?: () => void;
}) {
  const { icon: Icon, className: toneClassName } = NOTICE_TONES[tone];
  return (
    <div
      data-tone={tone}
      className={cn(NOTICE_CARD_CLASS, toneClassName, className)}
      {...props}
    >
      {/* アイコンと1行目の文字は、右端のボタン（32px）の中央の高さにそろえる。 */}
      <Icon aria-hidden="true" className="mt-2 size-4 shrink-0" />
      {/* 文の途中で折り返すときは、文節の切れ目で改行する。 */}
      <div className="min-w-0 flex-1 space-y-0.5 py-1.5 [word-break:auto-phrase]">
        <p
          className={cn(
            "break-words",
            description ? "font-semibold" : "font-medium",
          )}
        >
          {title}
        </p>
        {description ? (
          <p className="text-xs whitespace-pre-wrap break-words opacity-90">
            {description}
          </p>
        ) : null}
      </div>
      {/* 狭い画面では本文の幅を残すため、操作を本文の下の行へ回し、閉じるボタンだけを右上に残す。
          操作の文字は、ボタンの内側の余白を差し引いて本文の左端にそろえる。 */}
      {action ? (
        <div className="order-4 basis-full pl-3 sm:order-3 sm:basis-auto sm:pl-0">
          {action}
        </div>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={closeLabel}
        onClick={onClose}
        className="order-3 sm:order-4"
      >
        <XIcon aria-hidden="true" />
      </Button>
    </div>
  );
}

/**
 * 現れるときと閉じるときは、ドロップダウンと同じく上端から開いて上端へ畳む。closingにしたら
 * 閉じるアニメーションを再生し、終わったらonClosedを呼ぶ。動きを減らす設定ではアニメーションが
 * 無いので、すぐに呼ぶ。
 */
export function AnimatedNotice({
  closing,
  onClosed,
  className,
  ...props
}: ComponentProps<typeof NoticeCard> & {
  closing: boolean;
  onClosed: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const notifyClosed = useEffectEvent(onClosed);

  useEffect(() => {
    const element = ref.current;
    if (!closing || !element) return;
    let active = true;
    const finish = () => {
      if (active) notifyClosed();
    };
    void Promise.all(
      element.getAnimations().map((animation) => animation.finished),
    ).then(finish, finish);
    return () => {
      active = false;
    };
  }, [closing]);

  return (
    <NoticeCard
      ref={ref}
      data-open={closing ? undefined : ""}
      data-closed={closing ? "" : undefined}
      inert={closing || undefined}
      className={cn("origin-top", MENU_SIDE_ANIMATION_CLASS, className)}
      {...props}
    />
  );
}

/** トースト1件。成功と情報は時間がたつと閉じ、ポインターを重ねている間とフォーカスがある間は止める。 */
function ToastNotice({ item }: { item: ToastItem }) {
  const [closing, setClosing] = useState(false);
  const [paused, setPaused] = useState(false);
  const autoClose = item.tone !== "error";

  // 同じ文言をもう一度出したときはshownAtが変わり、表示時間をやり直す。
  useEffect(() => {
    if (!autoClose || paused || closing) return;
    const timer = window.setTimeout(() => setClosing(true), TOAST_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [autoClose, paused, closing, item.shownAt]);

  return (
    <AnimatedNotice
      tone={item.tone}
      title={item.message}
      closeLabel="通知を閉じる"
      closing={closing}
      onClose={() => setClosing(true)}
      onClosed={() => removeToast(item.id)}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(event: FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setPaused(false);
        }
      }}
    />
  );
}

const AnnouncementSlotContext = createContext<HTMLElement | null>(null);

/** お知らせを、通知の並びの先頭へ差し込む先。並びを描くまではnull。 */
export function useAnnouncementSlot() {
  return use(AnnouncementSlotContext);
}

/**
 * 通知の並び。画面の上端中央に、お知らせを先に、トーストをその下へ出した順に並べる。
 * app-rootはisolation: isolateで重なり順を閉じ込めるので、body直下に置いてダイアログより上に出す。
 * 通知の外側は下の画面をそのまま操作できる。
 */
function NoticeViewport({
  announcementSlotRef,
}: {
  announcementSlotRef: (element: HTMLElement | null) => void;
}) {
  const toasts = useSyncExternalStore(
    subscribeToasts,
    getToasts,
    getServerToasts,
  );
  const [body, setBody] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setBody(document.body);
  }, []);

  if (!body) return null;

  return createPortal(
    <section
      aria-label="通知"
      aria-live="polite"
      data-slot="notice-viewport"
      className="pointer-events-none fixed inset-x-0 top-2 z-[100] flex flex-col items-center gap-2 px-4"
    >
      {/* お知らせはページ全体への案内なので、ダイアログやシートを開いている間は隠し、その上端を覚わない。
          操作の結果を知らせるトーストは、ダイアログの上にも出す。 */}
      <div
        ref={announcementSlotRef}
        className="contents [body:has(:is([data-slot=dialog-content],[data-slot=alert-dialog-content],[data-slot=sheet-content])[data-open])_&]:hidden"
      />
      {toasts.map((item) => (
        <ToastNotice key={item.id} item={item} />
      ))}
    </section>,
    body,
  );
}

/** 通知の並びを置き、お知らせの差し込み先を配下へ渡す。 */
export function NoticeProvider({ children }: { children: ReactNode }) {
  const [announcementSlot, setAnnouncementSlot] = useState<HTMLElement | null>(
    null,
  );
  return (
    <AnnouncementSlotContext value={announcementSlot}>
      {children}
      <NoticeViewport announcementSlotRef={setAnnouncementSlot} />
    </AnnouncementSlotContext>
  );
}
